import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';

import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { UsersApi } from '../../../api/users/users.api';
import { AuthenticationError } from '../../services/error-handler.service';
import { McpOauthService } from '../../services/mcp-oauth.service';
import { WebsocketAwareNeptuneService } from '../../services/websocket-aware-neptune.service';
import { environment } from '../../../../environments/environment';


@Injectable()
export class LoginRouteGuard implements CanActivate {

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private usersApi: UsersApi,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private mcpOauthService: McpOauthService
    ) {
    }

    canActivate(next: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> {
        // MCP / Connector OAuth flow: when Kong redirects here with OAuth params, an
        // already-authenticated user must NOT be bounced into the app - we capture the
        // params and hand straight off to Kong's /oauth/complete. If there's no active
        // session we fall through to show the login page; the handoff then happens after
        // login (LoginService.postLogin). This is checked first so it pre-empts the
        // active-session redirect below.
        if (this.mcpOauthService.urlHasOauthParams()) {
            this.mcpOauthService.captureContextFromUrl();
            return this.usersApi.getCurrentUserState().pipe(
                map((state) => {
                    if (state) {
                        // Already logged in - complete the OAuth flow (apiKey rides in the cookie).
                        this.mcpOauthService.completeFlow();
                        return false;
                    }
                    return true;
                }),
                catchError((err) => {
                    if (err instanceof AuthenticationError) {
                        this.websocketAwareNeptuneService.disconnectFromWebsocket();
                        return of(true);
                    }
                    throw err;
                })
            );
        }

        // Application startup, we need to check if the user has a session:
        // 1. If they have a session, and they're in neptune only, then:
        //     a. If there is a return URL, we should attempt to follow it, if we fail, we should go to dashboard
        //     b. If there is not a return URL, we should go to dashboard
        // 2. If they have a session, and they're not in neptune only, we should return false, and wait for the other redirects to push them back to classic
        // 3. if they don't have a session, they should see the login page (return true)
        if (this.router.url === '/') {
            return this.usersApi.getCurrentUserState()
                .pipe(
                    map(
                        (state) => {
                            // Start case 1
                            if (state) {
                                if (state.currentUserAccount.account.neptuneOnly) {
                                    const returnUrlParam = new URLSearchParams(window.location.search).get('returnUrl');
                                    let returnUrl = null;
                                    try {
                                        returnUrl = new URL(returnUrlParam);
                                    } catch (e) {
                                    }
                                    if (returnUrl) {
                                        // If we get a returnUrl, navigate by it, if navigation fails, go to dashboard
                                        // Case 1.a
                                        this.router.navigateByUrl(returnUrl.pathname)
                                            .then((navigated) => {
                                                if (!navigated) {
                                                    this.router.navigate(['Dashboard']);
                                                }
                                            })
                                            .catch(() => {
                                                this.router.navigate(['Dashboard']);
                                            });
                                    } else {
                                        // Case 1b: No valid returnUrl found, go to dashboard
                                        this.router.navigate(['Dashboard']);
                                    }
                                }
                                // Case 2
                                return false;
                            }
                            // Case 3
                            return true;
                        }
                    ),
                    catchError((err) => {
                        if (err instanceof AuthenticationError) {
                            this.websocketAwareNeptuneService.disconnectFromWebsocket();
                            return of(true);
                        } else {
                            throw err;
                        }
                    })
                );
        }
        this.websocketAwareNeptuneService.disconnectFromWebsocket();
        return of(true);
    }
}
